import { RefObject, useEffect } from 'react'
import { VListHandle } from 'virtua'
import { KeyEvent } from '../../../constants/enums'
import { registerKeyHandler } from '../../../state/state.keys'

interface UseKeyboardNavigationParams {
    /** ref to virtua VList */
    listRef: RefObject<VListHandle>
    averageRowHeight: number
    containerHeight: number
    totalRowsHeight: number
}

/**
 * Scroll grid up/down using arrow keys, page up/down, home/end
 */
export const useKeyboardNavigation = (params: UseKeyboardNavigationParams) => {
    const { listRef, averageRowHeight, containerHeight, totalRowsHeight } =
        params

    useEffect(() => {
        const calculateScrollValue = (key: string) => {
            const { scrollOffset } = listRef.current

            switch (key) {
                case 'ArrowUp':
                    return Math.max(scrollOffset - averageRowHeight / 3, 0)
                case 'ArrowDown':
                    return Math.min(
                        scrollOffset + averageRowHeight / 3,
                        Math.floor(totalRowsHeight) - containerHeight,
                    )
                case 'PageUp':
                    return Math.max(scrollOffset - containerHeight, 0)
                case 'PageDown':
                    return Math.min(
                        scrollOffset + containerHeight,
                        Math.floor(totalRowsHeight) - containerHeight,
                    )
                case 'Home':
                    return 0
                case 'End':
                    return Math.floor(totalRowsHeight) - containerHeight
                default:
                    return null
            }
        }

        return registerKeyHandler(
            KeyEvent.Keydown,
            (key: string) => {
                if (!listRef.current) {
                    return
                }
                const scrollValue = calculateScrollValue(key)
                if (scrollValue !== null) {
                    listRef.current.scrollTo(scrollValue)
                }
            },
            ['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End'],
        )
    }, [listRef, averageRowHeight, containerHeight, totalRowsHeight])
}
